import { copy } from "../copy";
import Icon from "../components/Icon";
import type { Options, QueueFilters } from "./api";
import { ghostButton, inputClass, primaryButton } from "./ui";

const labelClass = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-chrome-dim";

// The search rail for the contest queues. Filtering happens on the server;
// the rail only builds the query.
export default function FilterRail({
  filters,
  options,
  found,
  onChange,
  onSearch,
  onReset,
}: {
  filters: QueueFilters;
  options: Options | null;
  found: number | null;
  onChange: (next: QueueFilters) => void;
  onSearch: () => void;
  onReset: () => void;
}) {
  const t = copy.admin.filters;
  const set = <K extends keyof QueueFilters>(key: K, value: QueueFilters[K]) => onChange({ ...filters, [key]: value });
  const range = (label: string, lo: "prizeMin" | "contestantsMin" | "admissionMin", hi: "prizeMax" | "contestantsMax" | "admissionMax") => (
    <fieldset>
      <legend className={labelClass}>{label}</legend>
      <div className="grid grid-cols-2 gap-2">
        <input
          aria-label={`${label} ${t.min}`}
          inputMode="decimal"
          placeholder={t.min}
          value={filters[lo]}
          onChange={(e) => set(lo, e.target.value)}
          className={inputClass}
        />
        <input
          aria-label={`${label} ${t.max}`}
          inputMode="decimal"
          placeholder={t.max}
          value={filters[hi]}
          onChange={(e) => set(hi, e.target.value)}
          className={inputClass}
        />
      </div>
    </fieldset>
  );
  const select = (label: string, key: "genre" | "challenge" | "duration", values: string[]) => (
    <div>
      <label htmlFor={`admin-${key}`} className={labelClass}>
        {label}
      </label>
      <select id={`admin-${key}`} value={filters[key]} onChange={(e) => set(key, e.target.value)} className={`${inputClass} cursor-pointer`}>
        <option value="">{t.any}</option>
        {values.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <form
      aria-label={t.heading}
      onSubmit={(e) => {
        e.preventDefault();
        onSearch();
      }}
      className="flex flex-col gap-4 rounded-2xl border border-ground-edge bg-ground-raised p-5"
    >
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome">
          <Icon name="filter" className="text-gold" />
          {t.heading}
        </p>
        {found !== null && <span className="bf-tabular text-[11px] uppercase tracking-[0.12em] text-gold">{t.found(found)}</span>}
      </div>
      <div>
        <label htmlFor="admin-producer" className={labelClass}>
          {t.producer}
        </label>
        <input
          id="admin-producer"
          list="admin-producers"
          placeholder={t.producerPlaceholder}
          value={filters.producer}
          onChange={(e) => set("producer", e.target.value)}
          className={inputClass}
        />
        <datalist id="admin-producers">
          {(options?.producers ?? []).map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
      </div>
      {range(t.prize, "prizeMin", "prizeMax")}
      {range(t.contestants, "contestantsMin", "contestantsMax")}
      {range(t.admission, "admissionMin", "admissionMax")}
      {select(t.genre, "genre", options?.genres ?? [])}
      {select(t.challenge, "challenge", options?.challenges ?? [])}
      {select(t.duration, "duration", options?.durations ?? [])}
      <label className="flex cursor-pointer items-center gap-2 text-[13px] text-chrome">
        <input type="checkbox" checked={filters.freeAdmission} onChange={(e) => set("freeAdmission", e.target.checked)} className="accent-money" />
        {t.freeAdmission}
      </label>
      <label className="flex cursor-pointer items-center gap-2 text-[13px] text-chrome">
        <input type="checkbox" checked={filters.versus} onChange={(e) => set("versus", e.target.checked)} className="accent-money" />
        {t.versus}
      </label>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={onReset} className={ghostButton}>
          {t.reset}
        </button>
        <button type="submit" className={primaryButton}>
          {t.search}
        </button>
      </div>
    </form>
  );
}
